import { useEffect, useState } from "react";
import { Caixa } from "./styled";

/**
 * Orientação de tamanho para imagens enviadas pelo painel (banner, foto do
 * apoiador): o tamanho ideal ANTES de escolher o arquivo e, depois de escolher, um
 * aviso NÃO bloqueante sobre as dimensões da imagem escolhida.
 *
 * As medidas dos perfis vêm de como a imagem é desenhada de verdade:
 *  - banner no app: Container 327 × 176 (content_main.dart) = 1,86:1; no site,
 *    CardApoiador com 215 px de altura e object-fit: cover, ~1,5:1. Nos dois a
 *    imagem é CORTADA para preencher. Ideal 1000 × 540 (~3x o app); mínimo 654 × 352
 *    (2x o app, para telas densas).
 *  - foto do apoiador: aparece em círculo no app; quadrada, ideal 400 × 400.
 */
export const PERFIL_BANNER = {
  titulo: "Tamanho ideal do banner",
  ideal: { largura: 1000, altura: 540 },
  minimo: { largura: 654, altura: 352 },
  texto: [
    "1000 × 540 px (proporção 1,85:1, a do app) · mínimo 654 × 352 px · PNG ou JPG até 10 MB.",
    "No app o banner aparece em 327 × 176 e no site um pouco mais alto (cerca de 1,5:1). A imagem é cortada para preencher, então deixe logo e texto no CENTRO.",
  ],
  pequena: "Imagem pequena: pode ficar borrada no app.",
  proporcao: "Proporção diferente da ideal: partes das bordas serão cortadas.",
  boa: "✓ Boa para o banner.",
};

export const PERFIL_FOTO_APOIADOR = {
  titulo: "Tamanho ideal da foto",
  ideal: { largura: 400, altura: 400 },
  minimo: { largura: 200, altura: 200 },
  texto: [
    "Quadrada, ideal 400 × 400 px (mínimo 200 × 200) · PNG ou JPG até 10 MB. Aparece em círculo no app.",
  ],
  pequena: "Imagem pequena: pode ficar borrada no app.",
  proporcao: "Não é quadrada: partes das bordas serão cortadas ao aparecer em círculo.",
  boa: "✓ Boa para a foto.",
};

// Quanto a proporção pode se afastar da ideal antes do aviso (25%).
export const TOLERANCIA_PROPORCAO = 0.25;

/**
 * Avalia as dimensões de uma imagem contra um perfil. Função pura.
 * Devolve { resumo, avisos: [texto], ok }; `ok` é verdadeiro sem nenhum aviso.
 */
export function avaliarImagem({ largura, altura }, perfil) {
  const resumo = `Imagem escolhida: ${largura} × ${altura} px`;
  const avisos = [];

  if (largura < perfil.minimo.largura) avisos.push(perfil.pequena);

  const razao = largura / altura;
  const ideal = perfil.ideal.largura / perfil.ideal.altura;
  if (Math.abs(razao - ideal) / ideal > TOLERANCIA_PROPORCAO) avisos.push(perfil.proporcao);

  return { resumo, avisos, ok: avisos.length === 0 };
}

// Lê largura e altura do arquivo no navegador. Falha (arquivo que não é imagem)
// devolve null: o formulário já recusa o arquivo por tipo/tamanho.
export function lerDimensoes(arquivo) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(arquivo);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ largura: img.naturalWidth, altura: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

export default function DicaImagem({ perfil, arquivo, id }) {
  const [avaliacao, setAvaliacao] = useState(null);

  useEffect(() => {
    let vivo = true;
    setAvaliacao(null);
    if (!arquivo) return undefined;
    lerDimensoes(arquivo).then((dim) => { if (vivo && dim) setAvaliacao(avaliarImagem(dim, perfil)); });
    return () => { vivo = false; };
  }, [arquivo, perfil]);

  return (
    <Caixa id={id}>
      <strong>{perfil.titulo}</strong>
      {perfil.texto.map((t) => <p key={t}>{t}</p>)}

      {avaliacao &&
        <div className="avaliacao" role="status">
          <span className="escolhida">{avaliacao.resumo}</span>
          {avaliacao.ok
            ? <span className="bom">{perfil.boa}</span>
            : avaliacao.avisos.map((a) => <span key={a} className="aviso">⚠ {a}</span>)}
        </div>
      }
    </Caixa>
  );
}
